<script setup>
import { ref, onMounted } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import FormInput from '@/Components/FormInput.vue'
import FormSelect from '@/Components/FormSelect.vue'
import FormTextarea from '@/Components/FormTextarea.vue'
import CustomLink from '@/Components/CustomLink.vue'
import Button from '@/Components/Button.vue'

const props = defineProps({
    projects: Array
})

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger)

// Add refs for contact section
const contactTitleRef = ref(null)
const contactSubtitleRef = ref(null) 
const contactFormRef = ref(null)
const contactStartAnimation = ref(null)
const formSubmitting = ref(false)
const formSuccess = ref(false)
const formError = ref(false)

// Form data
const contactForm = ref({
    name: '',
    email: '',
    phoneNumber: '',
    company: '',
    subject: '',
    message: '',
    acceptTerms: false
})

// Subject options for the dropdown
const subjectOptions = [
    { value: 'offerte', label: 'Offerte aanvraag' },
    { value: 'informatie', label: 'Informatie over diensten' },
    { value: 'samenwerking', label: 'Samenwerkingsmogelijkheden' },
    { value: 'support', label: 'Technische ondersteuning' },
    { value: 'vacature', label: 'Werkgelegenheid / Sollicitatie' },
    { value: 'feedback', label: 'Feedback geven' },
    { value: 'anders', label: 'Anders' }
]

// Update refs for office section
const infoTitleRef = ref(null)
const officeImageRef = ref(null)
const officeInfoRef = ref(null)
const officeMapRef = ref(null)
const officeSection = ref(null)

onMounted(() => {
    // Set initial states immediately to prevent flash
    gsap.set([infoTitleRef.value, officeImageRef.value, officeInfoRef.value, officeMapRef.value], { 
        opacity: 0 
    });
    gsap.set('.contact-card', { opacity: 0, y: 15 });
    
    // Also set contact form elements to opacity 0
    gsap.set([contactTitleRef.value, contactSubtitleRef.value, contactFormRef.value], {
        opacity: 0,
        y: 20
    });
    
    // Small delay to ensure DOM is ready
    setTimeout(() => {
        // Create a master timeline for main content animations (except contact form)
        const masterTl = gsap.timeline();
        
        // Fade in title section with a gentle animation
        masterTl.add(
            gsap.timeline()
            .fromTo(infoTitleRef.value, 
                { opacity: 0, y: 20 }, 
                { opacity: 1, y: 0, duration: 1.2, ease: 'power2.out' }
            )
        )
        
        // Fade in contact cards with slight stagger
        .add(
            gsap.timeline()
            .fromTo('.contact-card', 
                { opacity: 0, y: 15 }, 
                { 
                    opacity: 1, 
                    y: 0, 
                    duration: 0.8, 
                    stagger: 0.15, 
                    ease: 'power1.out' 
                }
            ), 
            "-=0.8"
        )
        
        // Fade in office section with slight delay
        .add(
            gsap.timeline()
            .fromTo(officeImageRef.value, 
                { opacity: 0, scale: 0.95 }, 
                { opacity: 1, scale: 1, duration: 1, ease: 'power1.out' }
            )
            .fromTo(officeInfoRef.value, 
                { opacity: 0, scale: 0.95 }, 
                { opacity: 1, scale: 1, duration: 1, ease: 'power1.out' },
                "-=0.8"
            )
            .fromTo(officeMapRef.value, 
                { opacity: 0, scale: 0.95 }, 
                { opacity: 1, scale: 1, duration: 1, ease: 'power1.out' },
                "-=0.8"
            ),
            "-=0.4"
        );
        
        // Create a separate timeline for contact form that triggers on scroll
        const contactTl = gsap.timeline({
            scrollTrigger: {
                trigger: contactStartAnimation.value,
                start: "top 70%",
                toggleActions: "play none none none",
            }
        });
        
        // Add fade-in animations for contact form elements
        contactTl
            .fromTo(contactTitleRef.value, 
                { opacity: 0, y: 20 }, 
                { opacity: 1, y: 0, duration: 1, ease: 'power2.out' }
            )
            .fromTo(contactSubtitleRef.value, 
                { opacity: 0, y: 15 }, 
                { opacity: 1, y: 0, duration: 1, ease: 'power1.out' },
                "-=0.7"
            )
            .fromTo(contactFormRef.value, 
                { opacity: 0, y: 15 }, 
                { opacity: 1, y: 0, duration: 1, ease: 'power1.out' },
                "-=0.7"
            );
    }, 100);
})

// Submit form function
function submitForm() {
    if (!contactForm.value.acceptTerms) {
        return;
    }
    
    formSubmitting.value = true;
    
    // Simulate API call with timeout
    setTimeout(() => {
        formSubmitting.value = false;
        formSuccess.value = true;
        
        // Reset form with all fields
        contactForm.value = {
            name: '',
            email: '',
            phoneNumber: '',
            company: '',
            subject: '',
            message: '',
            acceptTerms: false
        };
        
        // Reset success message after delay
        setTimeout(() => {
            formSuccess.value = false;
        }, 5000);
    }, 1500);
}
</script>

<template>
    <!-- Additional information -->
    <div class="wide-container mt-16 sm:mt-20 md:mt-28">
        <div ref="infoTitleRef" class="text-center">
            <h2 class="text-4xl font-semibold text-zinc-900">Contact</h2>
            <p class="mt-4 text-xl text-zinc-600 max-w-3xl mx-auto">Ontdek onze werkplek waar creativiteit en technologie samenkomen</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <!-- Office location card -->
            <CustomLink href="https://www.google.com/maps/dir/?api=1&destination=Brugstraat+11,+7607+XJ+Almelo,+Nederland&travelmode=driving" :useInertia="false" target="_blank" rel="noopener noreferrer" class="contact-card group/card relative bg-white p-6 rounded-xl shadow-sm ring-zinc-100 ring-1 overflow-hidden cursor-pointer transition-all duration-500 ease-[cubic-bezier(.3,.7,.4,1)] hover:-translate-y-[5px] hover:shadow-[0_15px_30px_rgba(0,0,0,0.1)]">
                <div class="contact-card-content relative z-10">
                    <div class="flex items-center mb-4">
                        <font-awesome-icon :icon="['fas', 'map-location-dot']" class="h-6 w-6 text-green-600/65 group-hover:card:text-green-600 mr-3 group-hover/card:scale-125 transition-all duration-500 ease-smooth" />
                        <h3 class="text-xl font-medium text-zinc-900">Bezoekadres</h3>
                    </div>
                    <div class="space-y-1">
                        <p class="text-zinc-600 transition-all duration-500 ease-smooth group-hover/card:translate-x-2">Brugstraat 11</p>
                        <p class="text-zinc-600 transition-all duration-500 ease-smooth group-hover/card:translate-x-2 group-hover/card:delay-[100ms]">7607 XJ Almelo</p>
                        <p class="text-zinc-600 transition-all duration-500 ease-smooth group-hover/card:translate-x-2 group-hover/card:delay-[200ms]">Nederland</p>
                    </div>
                </div>
                <div class="z-[1] absolute inset-0 bg-gradient-to-br from-lime-50/0 via-lime-50/0 to-emerald-50/0 opacity-0 group-hover/card:opacity-100 group-hover/card:from-lime-50/20 group-hover/card:via-lime-50/30 group-hover/card:to-emerald-50 transition-all duration-700 ease-in-out"></div>
                <div class="z-[2] absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-emerald-600 transform scale-x-0 origin-left group-hover/card:scale-x-100 transition-transform duration-500 ease-smooth"></div>
            </CustomLink>
            
            <!-- Phone info card -->
            <CustomLink href="tel:0534567890" :useInertia="false" class="contact-card group/card relative bg-white p-6 rounded-xl shadow-sm ring-zinc-100 ring-1 overflow-hidden transition-all duration-500 ease-[cubic-bezier(.3,.7,.4,1)] hover:-translate-y-[5px] hover:shadow-[0_15px_30px_rgba(0,0,0,0.1)]">
                <div class="contact-card-content relative z-10">
                    <div class="flex items-center mb-4">
                        <font-awesome-icon :icon="['fas', 'phone']" class="h-6 w-6 text-green-600/65 group-hover:card:text-green-600 mr-3 group-hover/card:scale-125 transition-all duration-500 ease-smooth" />
                        <h3 class="text-xl font-medium text-zinc-900">Telefoon</h3>
                    </div>
                    <div class="space-y-1">
                        <p class="text-zinc-600 transition-all duration-500 ease-smooth group-hover/card:translate-x-2"><CustomLink href="tel:0534567890" :useInertia="false" class="text-green-600 hover:underline group-hover/card:text-green-700">053-456 7890</CustomLink></p>
                        <p class="text-zinc-600 transition-all duration-500 ease-smooth group-hover/card:translate-x-2 group-hover/card:delay-[100ms]">Beschikbaar: Ma-Vr, 9:00-17:00</p>
                        <p class="text-zinc-600 transition-all duration-500 ease-smooth group-hover/card:translate-x-2 group-hover/card:delay-[200ms]">Ook bereikbaar via<CustomLink href="tel:0534567890" :useInertia="false" class="text-green-600 hover:underline group-hover/card:text-green-700"> Whatsapp</CustomLink></p>
                    </div>
                </div>
                <div class="z-[1] absolute inset-0 bg-gradient-to-br from-lime-50/0 via-lime-50/0 to-emerald-50/0 opacity-0 group-hover/card:opacity-100 group-hover/card:from-lime-50/20 group-hover/card:via-lime-50/30 group-hover/card:to-emerald-50 transition-all duration-700 ease-in-out"></div>
                <div class="z-[2] absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-emerald-600 transform scale-x-0 origin-left group-hover/card:scale-x-100 transition-transform duration-500 ease-smooth"></div>
            </CustomLink>
            
            <!-- Email card -->
            <CustomLink href="mailto:info@codes.nl" :useInertia="false" class="contact-card group/card relative bg-white p-6 rounded-xl shadow-sm ring-zinc-100 ring-1 overflow-hidden cursor-pointer transition-all duration-500 ease-[cubic-bezier(.3,.7,.4,1)] hover:-translate-y-[5px] hover:shadow-[0_15px_30px_rgba(0,0,0,0.1)]">
                <div class="contact-card-content relative z-10">
                    <div class="flex items-center mb-4">
                        <font-awesome-icon :icon="['fas', 'envelope']" class="h-6 w-6 text-green-600/65 group-hover:card:text-green-600 mr-3 group-hover/card:scale-125 transition-all duration-500 ease-smooth" />
                        <h3 class="text-xl font-medium text-zinc-900">Email</h3>
                    </div>
                    <div class="space-y-1">
                        <p class="text-zinc-600 transition-all duration-500 ease-smooth group-hover/card:translate-x-2"><span class="text-green-600 group-hover/card:text-green-700">info@codes.nl</span></p>
                        <p class="text-zinc-600 transition-all duration-500 ease-smooth group-hover/card:translate-x-2 group-hover/card:delay-[100ms]">Reactie binnen 24 uur</p>
                        <p class="text-zinc-600 transition-all duration-500 ease-smooth group-hover/card:translate-x-2 group-hover/card:delay-[200ms]">Voor dringende zaken, bel ons</p>
                    </div>
                </div>
                <div class="z-[1] absolute inset-0 bg-gradient-to-br from-lime-50/0 via-lime-50/0 to-emerald-50/0 opacity-0 group-hover/card:opacity-100 group-hover/card:from-lime-50/20 group-hover/card:via-lime-50/30 group-hover/card:to-emerald-50 transition-all duration-700 ease-in-out"></div>
                <div class="z-[2] absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-emerald-600 transform scale-x-0 origin-left group-hover/card:scale-x-100 transition-transform duration-500 ease-smooth"></div>
            </CustomLink>
        </div>
    </div>

    <div ref="officeSection" class="wide-container">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-y-4 gap-x-10">
            <!-- Main office image -->
            <div ref="officeImageRef" class="group/image relative h-80 w-full rounded-2xl overflow-hidden ring-1 ring-zinc-100 shadow-sm order-1 transition-all duration-500 ease-[cubic-bezier(.3,.7,.4,1)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.1)]">
                <div class="absolute inset-0 overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="c-o-d-e-s kantoor" class="w-full h-full object-cover transition-all duration-500 ease-out group-hover/image:scale-[102%]" />
                </div>
            </div>
            <div class="grid grid-cols-2 gap-4 order-4 lg:order-3">
                <div class="group/image h-72 rounded-xl overflow-hidden ring-1 ring-zinc-100 shadow-sm transition-all duration-500 ease-[cubic-bezier(.3,.7,.4,1)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.1)]">
                    <div class="h-full w-full overflow-hidden">
                        <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Team aan het werk" class="w-full h-full object-cover transition-all duration-500 ease-out group-hover/image:scale-[102%]" />
                    </div>
                </div>
                <div class="group/image h-72 rounded-xl overflow-hidden ring-1 ring-zinc-100 shadow-sm transition-all duration-500 ease-[cubic-bezier(.3,.7,.4,1)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.1)]">
                    <div class="h-full w-full overflow-hidden">
                        <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Samenwerkingsruimte" class="w-full h-full object-cover transition-all duration-500 ease-out group-hover/image:scale-[102%]" />
                    </div>
                </div>
            </div>
            
            <!-- Office information -->
            <div ref="officeInfoRef" style="opacity: 0;" class="group/info h-80 flex flex-col bg-white ring-1 ring-zinc-100 p-6 rounded-2xl shadow-none order-2 lg:order-2 relative overflow-hidden transition-all duration-500 ease-[cubic-bezier(.3,.7,.4,1)] hover:-translate-y-[5px] hover:shadow-[0_15px_30px_rgba(0,0,0,0.1)]">
                <div class="z-10 relative h-full flex flex-col">
                    <h3 class="text-xl font-medium text-zinc-900 mb-4">Werkuren & Bereikbaarheid</h3>
                    <div class="flex flex-col justify-between grow">
                        <div class="space-y-1">
                            <div class="flex justify-between"><span class="text-zinc-600">Maandag - Vrijdag</span><span class="font-medium">9:00 - 17:00</span></div>
                            <div class="flex justify-between"><span class="text-zinc-600">Zaterdag</span><span class="font-medium">Op afspraak</span></div>
                            <div class="flex justify-between"><span class="text-zinc-600">Zondag</span><span class="font-medium">Gesloten</span></div>
                        </div>
                        <div class="border-t border-zinc-100 transition-all duration-500 group-hover/info:border-green-100"></div>
                        <div>
                            <h4 class="font-medium mb-2">Transport</h4>
                            <ul class="space-y-1">
                                <li class="flex items-center text-zinc-600"><font-awesome-icon :icon="['fas', 'car']" class="h-4 w-4 mr-2 text-green-600 transition-all duration-500 ease-smooth group-hover/info:scale-110" />Gratis parkeren beschikbaar</li>
                                <li class="flex items-center text-zinc-600"><font-awesome-icon :icon="['fas', 'bus']" class="h-4 w-4 mr-2 text-green-600 transition-all duration-500 ease-smooth group-hover/info:scale-110" />Bushalte direct voor de deur</li>
                                <li class="flex items-center text-zinc-600"><font-awesome-icon :icon="['fas', 'train']" class="h-4 w-4 mr-2 text-green-600 transition-all duration-500 ease-smooth group-hover/info:scale-110" />Station Almelo op 5 minuten loop afstand</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="absolute inset-0 bg-gradient-to-br from-green-50/0 to-green-100/0 group-hover/info:from-green-0 group-hover/info:to-green-50/50 transition-all duration-700 ease-smooth"></div>
            </div>
                
            <div ref="officeMapRef" class="group/map h-72 flex flex-col bg-white p-6 rounded-2xl ring-1 ring-zinc-100 shadow-none order-3 lg:order-4 relative overflow-hidden transition-all duration-500 ease-[cubic-bezier(.3,.7,.4,1)] hover:-translate-y-[5px] hover:shadow-[0_15px_30px_rgba(0,0,0,0.1)]">
                <div class="z-10 relative">
                    <h3 class="text-xl font-medium text-zinc-900 mb-4">Locatie</h3>
                </div>
                <div class="flex grow rounded-lg overflow-hidden bg-zinc-100 z-10 relative group/mapcontainer transition-all duration-500 ease-smooth ring-0 hover:ring-2 hover:ring-green-800">
                    <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2440.3021068823!2d6.6653867!3d52.356795!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b810c48d7b5815%3A0x7fe5c9e3ad0e836d!2sStation%20Almelo!5e0!3m2!1sen!2snl!4v1689764422156!5m2!1sen!2snl" width="100%" height="100%" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade" class="transition-transform duration-500 ease-out group-hover/mapcontainer:scale-[1.02]"></iframe>
                </div>
                <div class="absolute inset-0 bg-gradient-to-br from-green-50/0 to-green-100/0 group-hover/map:from-green-0 group-hover/map:to-green-50/50 transition-all duration-700 ease-smooth"></div>
            </div>
        </div>
    </div>

    <!-- Contact Section -->
    <div ref="contactStartAnimation" id="contactForm" class="wide-container">
        <div class="flex flex-col items-center">
            <h2 ref="contactTitleRef" class="text-pretty text-5xl font-semibold text-zinc-900 sm:text-6xl max-w-4xl tracking-tight leading-[120%]">Samenwerken aan jouw succes</h2>
            <p ref="contactSubtitleRef" class="text-balance text-xl sm:font-medium text-zinc-600 max-w-2xl mt-6">Heb je een idee of een vraag? Wij helpen je graag verder. Laat je gegevens achter en we nemen zo snel mogelijk contact met je op.</p> 
            
            <form ref="contactFormRef" @submit.prevent="submitForm" class="mt-16 w-full max-w-3xl animated-gradient-medium rounded-2xl p-10 shadow-xl">
                <div class="space-y-4">
                    <!-- Personal Information Section -->
                    <div>
                        <h3 class="text-lg font-medium text-white mb-4">Je gegevens</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormInput v-model="contactForm.name" id="name" type="text" placeholder="Jouw naam" :required="true" icon="user" />
                            <FormInput v-model="contactForm.email" id="email" type="email" placeholder="E-mailadres" :required="true" icon="envelope" />
                            <FormInput v-model="contactForm.phoneNumber" id="phoneNumber" type="tel" placeholder="Telefoonnummer" :required="true" icon="phone" />
                            <FormInput v-model="contactForm.company" id="company" type="text" placeholder="Bedrijfsnaam" :required="false" icon="building" />
                        </div>
                    </div>
                    
                    <!-- Subject Selection Dropdown -->
                    <div>
                        <h3 class="text-lg font-medium text-white mb-4">Je bericht</h3>
                        <FormSelect v-model="contactForm.subject" id="subject" :options="subjectOptions" placeholder="Selecteer een onderwerp" :required="true" icon="tag" />
                    </div>
                    
                    <!-- Message Section -->
                    <div>
                        <FormTextarea v-model="contactForm.message" id="message" placeholder="Vertel ons waar we je mee kunnen helpen" :required="true" :rows="4" icon="message" />
                    </div>
                    
                    <div class="flex justify-between">
                        <!-- Terms and Conditions -->
                        <div class="flex items-start">
                            <div class="flex">
                                <input id="acceptTerms" type="checkbox" v-model="contactForm.acceptTerms" required class="h-4 w-4 rounded border-zinc-300 text-emerald-400 accent-white focus:ring-emerald-400 focus:ring-offset-green-800 focus:outline-none" />
                            </div>
                            <div class="ml-3 text-sm">
                                <label for="acceptTerms" class="text-white font-medium">Ik ga akkoord met de <CustomLink href="/terms" class="text-white underline hover:text-lime-200 transition-colors duration-300" :useInertia="true">algemene voorwaarden</CustomLink></label>
                            </div>
                        </div>
                        <Button type="submit" variant="white" :disabled="formSubmitting || !contactForm.acceptTerms" :loading="formSubmitting" :class="[!contactForm.acceptTerms ? 'opacity-70' : '']" >Versturen</Button>
                    </div>
                    
                    <!-- Success message with enhanced styling -->
                    <div v-if="formSuccess" class="p-6 bg-gradient-to-r from-green-50 to-lime-50 rounded-xl text-green-800 text-sm border border-green-200 shadow-sm transform transition-all duration-500 animate-[fadeIn_0.4s_ease-out_forwards] hover:shadow-md">
                        <div class="flex items-center">
                            <div class="bg-lime-100 rounded-full p-2 mr-4">
                                <font-awesome-icon :icon="['fas', 'check-circle']" class="h-5 w-5 text-lime-500" />
                            </div>
                            <div>
                                <h4 class="font-medium text-base mb-1">Bericht verstuurd!</h4>
                                <p class="text-zinc-600">Bedankt voor je bericht! We nemen snel contact met je op.</p>
                            </div>
                        </div>
                    </div>

                    <!-- Error message with enhanced styling -->
                    <div v-if="formError" class="p-6 bg-gradient-to-r from-red-50 to-rose-50 rounded-xl text-rose-800 text-sm border border-red-200 shadow-sm transform transition-all duration-500 animate-[fadeIn_0.4s_ease-out_forwards] hover:shadow-md">
                        <div class="flex items-center">
                            <div class="bg-rose-100 rounded-full p-2 mr-4">
                                <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="h-5 w-5 text-rose-500" />
                            </div>
                            <div>
                                <h4 class="font-medium text-base mb-1">Er is iets misgegaan</h4>
                                <p class="text-zinc-600">Probeer het later nog eens of neem telefonisch contact op.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>
